import _ from 'lodash';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, remove } from '../api';
import type { MatchOverride, NewMatchOverride, Project, Revision, Task } from '../../types';
import { nuc } from '../../utils';
import { errorMessageFromResponse } from '../../helpers/errors';

/**
 * Strips the `Response` container from around the data.  Failed responses are
 * rejected. The resulting promise should only contain a non-nullable value.
 * The rejection should always be an error.
 */
async function stripResponse<T>(fetch: Promise<Response & { data?: T }>): Promise<NonNullable<T>> {
  try {
    const response = await fetch;

    if (!response.data) {
      throw new Error('No data returned');
    }

    return response.data;
  } catch (e) {
    if (_.isError(e)) {
      return Promise.reject(e);
    }

    if (_.isString(e)) {
      throw new Error(e);
    }

    if (e instanceof Response) {
      throw new Error(errorMessageFromResponse(e));
    }

    throw new Error(`Unknown value as error: ${e}`);
  }
}

async function getProject(projectLocator: string): Promise<Project> {
  return stripResponse(get<Project>(
    `/api/projects/${nuc(projectLocator)}`,
    true,
  ));
}

async function getRevision(revisionLocator: string): Promise<Revision> {
  return stripResponse(get<Revision>(
    `/api/revisions/${nuc(revisionLocator)}`,
    true,
  ));
}

async function getMatchOverrides(projectLocator: string) {
  const data = await stripResponse(get<{ matchOverrides: MatchOverride[] }>(
    `/api/projects/${nuc(projectLocator)}/match_overrides`,
    true,
  ));

  return data.matchOverrides;
}

async function postMatchOverride(newMatchOverride: NewMatchOverride) {
  const data = await stripResponse(post<NewMatchOverride, { matchOverride: MatchOverride }>(
    `/api/projects/${nuc(newMatchOverride.componentProjectId)}/match_overrides`,
    newMatchOverride,
  ));

  return data.matchOverride;
}

async function deleteMatchOverride(matchOverrideId: number) {
  await stripResponse(remove<void, void>(
    `/api/match_overrides/${matchOverrideId}`,
  ));
}

/**
 * Requests that a revision be rebuild.
 * Note: This is a GET request for some reason.
 * TODO: The request and response should be fully typed.
 */
export async function triggerRebuild(projectValue: string): Promise<Task> {
  const resp = await stripResponse<{task: Task}>(get(`/api/projects/${nuc(projectValue)}/rebuild`));
  if (!resp) {
    throw new Error('No task returned from rebuild');
  }
  return resp?.task;
}

/** An async thunk that fetches a project from a locator. */
export const fetchProject = createAsyncThunk('globalLicenseCorrections/fetchProject', getProject);

/** An async thunk that fetches a revision from a locator. */
export const fetchRevision = createAsyncThunk('globalLicenseCorrections/fetchRevision', getRevision);

export const createNewMatchOverride = createAsyncThunk('globalLicenseCorrections/postNewMatchOverride', postMatchOverride);

/** An async thunk that removes a match override. */
export const removeMatchOverride = createAsyncThunk('globalLicenseCorrections/removeMatchOverride', deleteMatchOverride);

export const fetchOverrides = createAsyncThunk('globalLicenseCorrections/fetchOverrides', getMatchOverrides);

export const fetchLicenses = createAsyncThunk(
  'globalLicenseCorrections/fetchLicenseGroups',
  async (revisionLocator: string) => {
    const revision = await stripResponse(get<Revision>(`/api/revisions/${nuc(revisionLocator)}/license-groups`));
    return revision.LicenseGroups?.flatMap(lg => lg.RevisionLicenses ?? []) ?? [];
  },
);
